import type { PagedFlipPageSlot, Template, TemplateNode } from "../types/template";
import {
  getTemplatePagedNodes,
  PAGED_FLIP_PAGE_SLOTS,
} from "./pagedFlipTemplate";
import { resolveTemplateEngine } from "./templateEngine";

export type TemplateValidationIssue = {
  level: "error" | "warning";
  message: string;
  nodeId?: string;
};

export type TemplateValidationResult = {
  valid: boolean;
  issues: TemplateValidationIssue[];
};

function walkNodes(nodes: TemplateNode[] | undefined, visitor: (node: TemplateNode) => void) {
  if (!Array.isArray(nodes)) return;
  for (const node of nodes) {
    visitor(node);
    if (Array.isArray(node.children) && node.children.length > 0) {
      walkNodes(node.children, visitor);
    }
  }
}

function validatePagedFlipNodeList(
  slot: PagedFlipPageSlot,
  nodes: TemplateNode[],
  issues: TemplateValidationIssue[],
) {
  walkNodes(nodes, (node) => {
    if (node.type === "img") {
      const hasBindingSrc = typeof node.props?.bindings?.src === "string";
      const hasStaticSrc = typeof node.props?.src === "string" && node.props.src.trim().length > 0;
      const hasImageBinding = typeof node.props?.imageBinding === "string";

      if (!hasBindingSrc && !hasStaticSrc && !hasImageBinding) {
        issues.push({
          level: "warning",
          nodeId: node.id,
          message: `Image node in paged slot "${slot}" has no src, binding, or imageBinding.`,
        });
      }
    }
  });
}

function validatePagedFlipTemplate(template: Template, issues: TemplateValidationIssue[]) {
  const pagedFlip = template?.pagedFlip;

  if (!pagedFlip) {
    issues.push({
      level: "warning",
      message: "Paged flip template has no pagedFlip config. Default flipbook behavior will be used.",
    });
  }

  if (pagedFlip?.showThumbnailRail) {
    issues.push({
      level: "warning",
      message:
        "Paged flip template enables thumbnail rail. Current product direction prefers a cleaner flipbook shell without thumbnail rails.",
    });
  }

  const hasAnyExplicitFlipNodes = PAGED_FLIP_PAGE_SLOTS.some(
    (slot) => getTemplatePagedNodes(template, slot).length > 0,
  );

  if (!hasAnyExplicitFlipNodes) {
    issues.push({
      level: "warning",
      message:
        "Paged flip template has no explicit page node definitions. It will render with the built-in fallback layout instead of a template-specific book design.",
    });
  }

  if (Array.isArray(template?.pagedNodes)) {
    const invalidSlotNodes = template.pagedNodes.filter((node) => {
      const slotValue = node.props?.pageSlot;
      if (slotValue == null) return false;

      const slots = Array.isArray(slotValue) ? slotValue : [slotValue];
      return slots.some((slot) => !PAGED_FLIP_PAGE_SLOTS.includes(slot as PagedFlipPageSlot));
    });

    invalidSlotNodes.forEach((node) => {
      issues.push({
        level: "error",
        nodeId: node.id,
        message: `Paged flip node has unknown pageSlot "${String(node.props?.pageSlot)}".`,
      });
    });
  }

  PAGED_FLIP_PAGE_SLOTS.forEach((slot) => {
    const nodes = getTemplatePagedNodes(template, slot);
    if (nodes.length > 0) {
      validatePagedFlipNodeList(slot, nodes, issues);
    }
  });

  if (getTemplatePagedNodes(template, "content").length === 0) {
    issues.push({
      level: "warning",
      message: "Paged flip template has no content slot nodes. Content pages will fall back to the built-in item layout.",
    });
  }

  if (pagedFlip?.showCategorySpotlights && getTemplatePagedNodes(template, "category-spotlight").length === 0) {
    issues.push({
      level: "warning",
      message:
        "Paged flip template enables category spotlights but has no category-spotlight slot nodes. Spotlight pages will use the built-in fallback layout.",
    });
  }
}

export function validateTemplateSchema(template: Template): TemplateValidationResult {
  const issues: TemplateValidationIssue[] = [];
  const engine = resolveTemplateEngine(template);

  if (!template?.meta?.name?.trim()) {
    issues.push({ level: "error", message: "Template meta.name is required." });
  }

  if (!template?.meta?.slug?.trim()) {
    issues.push({ level: "error", message: "Template meta.slug is required." });
  }

  if (
    engine === "legacy-scroll" &&
    (!Array.isArray(template?.nodes) || template.nodes.length === 0)
  ) {
    issues.push({ level: "error", message: "Template must include at least one root node." });
  }

  if (!template?.canvas?.width || !template?.canvas?.height) {
    issues.push({
      level: "warning",
      message: "Template canvas width/height are missing. Responsive scaling may be inaccurate.",
    });
  }

  walkNodes(template.nodes, (node) => {
    const repeatFor = node.props?.bindings?.repeatFor;

    if (node.props?.repeater && repeatFor === "menu.categoriesBySection") {
      const hasSectionFilter = Boolean(
        (typeof node.props?.sectionId === "string" && node.props.sectionId.trim()) ||
          (typeof node.props?.sectionIdBinding === "string" && node.props.sectionIdBinding.trim()) ||
          (typeof node.props?.bindings?.sectionId === "string" && node.props.bindings.sectionId.trim()),
      );
      if (!hasSectionFilter) {
        issues.push({
          level: "warning",
          nodeId: node.id,
          message:
            "Repeater uses menu.categoriesBySection without section filter. Consider sectionId or sectionIdBinding for deterministic grouping.",
        });
      }
    }

    if (node.type === "img") {
      const hasBindingSrc = typeof node.props?.bindings?.src === "string";
      const hasStaticSrc = typeof node.props?.src === "string" && node.props.src.trim().length > 0;
      const hasImageBinding = typeof node.props?.imageBinding === "string";

      if (node.props?.imageBinding === "random") {
        issues.push({
          level: "error",
          nodeId: node.id,
          message: "Random image binding is not allowed. Define explicit image URLs or deterministic bindings.",
        });
      }

      if (!hasBindingSrc && !hasStaticSrc && !hasImageBinding) {
        issues.push({
          level: "warning",
          nodeId: node.id,
          message: "Image node has no src, binding, or imageBinding. It will render empty.",
        });
      }

      const IMAGE_SHAPES = ["circle", "rounded", "square", "plate", "polaroid"];
      const CUTOUT_MODES = ["prefer", "require", "never"];
      if (typeof node.props?.shape === "string" && !IMAGE_SHAPES.includes(node.props.shape)) {
        issues.push({
          level: "error",
          nodeId: node.id,
          message: `Unknown image shape "${node.props.shape}". Use one of: ${IMAGE_SHAPES.join(", ")}.`,
        });
      }
      if (typeof node.props?.cutout === "string" && !CUTOUT_MODES.includes(node.props.cutout)) {
        issues.push({
          level: "error",
          nodeId: node.id,
          message: `Unknown cutout mode "${node.props.cutout}". Use one of: ${CUTOUT_MODES.join(", ")}.`,
        });
      }
    }

    if ((node.props?.splitItemsColumns || node.props?.splitCategoriesColumns) && !template?.responsive?.splitColumnsBreakpoint) {
      issues.push({
        level: "warning",
        nodeId: node.id,
        message: "Split column node found without responsive.splitColumnsBreakpoint. Default breakpoint will be used.",
      });
    }

    const LAYOUT_MODES = ["row", "column", "stack", "two-column", "grid", "zigzag", "masonry", "shelf", "scatter"];
    if (typeof node.props?.layout === "string" && node.props.layout.trim()) {
      const mode = node.props.layout.trim().toLowerCase();
      const known = LAYOUT_MODES.includes(mode) || /^grid\(\d{1,3}\)$/.test(mode);
      if (!known) {
        issues.push({
          level: "warning",
          nodeId: node.id,
          message: `Unknown layout "${node.props.layout}". Expected row, column, stack, two-column, grid, grid(n), zigzag, masonry, shelf, scatter, or undefined.`,
        });
      }
    }
  });

  if (engine === "paged-flip") {
    validatePagedFlipTemplate(template, issues);
  }

  return {
    valid: !issues.some((issue) => issue.level === "error"),
    issues,
  };
}

export function validateLegacyTemplateSchema(template: Template): TemplateValidationResult {
  return validateTemplateSchema(template);
}
